import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { QueryAutoCompleteResultDto } from '@shared/dtos/query-auto-complete-result.dto';
import { PostAutoCompleteRequestDto } from '@shared/dtos/post-auto-complete-request.dto';
import { PostBuildingVerificationQueryParams } from '@shared/dtos/post-building-verification-query-params.dto';
import { QueryBuildingVerificationResult } from '@shared/dtos/query-building-verification-result.dto';
import { ADDRESS_COMPLETION_OPTIONS } from '../address-completion.token';
import { AddressCompletionOptions } from '../interfaces/address-completion-options.interface';
import { mergeMap } from 'rxjs/operators';
import { CantonDto } from '@shared/dtos/canton.dto';
import { ConfigService } from '../../shared/services/config.service';

interface Address {
  street: string;
  streetNumber: string;
  postCode: string;
  canton: string;
  locality: string;
}

@Injectable()
export class AddressCompletionService {
  constructor(
    private httpClient: HttpClient,
    private configService: ConfigService,
    @Inject(ADDRESS_COMPLETION_OPTIONS)
    private readonly options: AddressCompletionOptions
  ) {}

  public autoComplete(
    request: PostAutoCompleteRequestDto
  ): Observable<QueryAutoCompleteResultDto> {
    return this.httpClient.post<QueryAutoCompleteResultDto>(
      `${this.options.webServiceUrl}/autocomplete`,
      request
    );
  }

  validateAddress(
    address: Address,
    retryAttempt: number = 0
  ): Observable<boolean> {
    const houseNo =
      address.streetNumber &&
      (address.streetNumber.match(/\d+/g) as RegExpMatchArray);
    const houseNoAddition =
      address.streetNumber &&
      (address.streetNumber.match(/[a-zA-Z]+/g) as RegExpMatchArray);

    const queryParams: PostBuildingVerificationQueryParams = {
      streetname: address.street,
      houseno: (houseNo && houseNo[0]) || '',
      housenoaddition: (houseNoAddition && houseNoAddition[0]) || '',
      zipcode: address.postCode,
      canton: address.canton,
      townName: address.locality,
    };

    return this.httpClient
      .get<QueryBuildingVerificationResult>(
        `${this.options.webServiceUrl}/verify`,
        {
          params: queryParams as any,
        }
      )
      .pipe(
        mergeMap((res) => {
          // Retry by adding zip addition to the locality if PSTAT === 3
          if (
            res.BuildingVerificationData.PSTAT === 3 &&
            res.BuildingVerificationData.ZipAddition &&
            retryAttempt === 0
          ) {
            const addressCopy = { ...address };
            addressCopy.locality = `${addressCopy.locality} ${res.BuildingVerificationData.ZipAddition}`;
            return this.validateAddress(addressCopy, 1);
          }
          return of(
            // The address is correct for 'Street name', 'House number','Postcode' and 'Location'.
            res.BuildingVerificationData.PSTAT === 1 ||
              // The address could be normalised according to Post CH Ltd.'s guidelines for addresses.
              res.BuildingVerificationData.PSTAT === 2
          );
        })
      );
  }

  public getCantonsList(): Observable<CantonDto[]> {
    return this.httpClient
      .get<CantonDto[]>(this.configService.getApiBaseUrl() + '/address/canton/fr');
  }
}
